import { DatabaseOutlined } from '@ant-design/icons';
import { Alert, Descriptions, Modal, Table, Typography } from 'antd';
import PropTypes from 'prop-types';
import React from 'react';
import styled from 'styled-components';
import { useCachedDataset } from '../../../../api/Numina';
import Loading from '../../../../components/Loading';

const { Text, Paragraph } = Typography;

const StyledModal = styled(Modal)`
  .ant-modal-content {
    max-height: 80vh;
  }
  .ant-modal-body {
    max-height: calc(80vh - 110px);
    overflow-y: auto;
  }
`;

const StyledDescriptions = styled(Descriptions)`
  margin-bottom: 16px;
`;

const QueryText = styled(Paragraph)`
  background: #f5f5f5;
  padding: 12px;
  border-radius: 4px;
  font-family: monospace;
  white-space: pre-wrap;
  word-break: break-word;
`;

function DatasetModal({ resultKey = null, open, onClose }) {
  const { data, isLoading } = useCachedDataset(resultKey, {
    enabled: open && !!resultKey,
  });

  const dataset = data?.data;

  // Prepare table data from sample_rows
  const tableData = React.useMemo(() => {
    if (!dataset?.sample_rows || dataset.sample_rows.length === 0) {
      return { columns: [], dataSource: [] };
    }

    const rows = dataset.sample_rows;
    const columns = Object.keys(rows[0] || {}).map((key) => ({
      title: key,
      dataIndex: key,
      key,
      ellipsis: true,
      width: 150,
    }));

    const dataSource = rows.map((row, index) => ({
      ...row,
      key: index,
    }));

    return { columns, dataSource };
  }, [dataset]);

  return (
    <StyledModal
      title={
        <>
          <DatabaseOutlined /> Dataset: {resultKey}
        </>
      }
      open={open}
      onCancel={onClose}
      footer={null}
      width={900}
      destroyOnClose
    >
      {(() => {
        if (isLoading) {
          return <Loading />;
        }
        if (!dataset) {
          return <Alert message="Dataset not found" type="error" />;
        }
        return (
          <>
            <StyledDescriptions bordered column={1} size="small">
              <Descriptions.Item label="Result Key">
                <Text code>{dataset.result_key}</Text>
              </Descriptions.Item>
              {dataset.metadata && (
                <>
                  {dataset.metadata.row_count !== undefined && (
                    <Descriptions.Item label="Row Count">
                      {dataset.metadata.row_count}
                    </Descriptions.Item>
                  )}
                  {dataset.metadata.column_count !== undefined && (
                    <Descriptions.Item label="Column Count">
                      {dataset.metadata.column_count}
                    </Descriptions.Item>
                  )}
                  {dataset.metadata.columns && (
                    <Descriptions.Item label="Columns">
                      {dataset.metadata.columns.join(', ')}
                    </Descriptions.Item>
                  )}
                </>
              )}
              <Descriptions.Item label="Created">
                {new Date(dataset.created_at).toLocaleString()}
              </Descriptions.Item>
            </StyledDescriptions>

            {dataset.nl_query && (
              <>
                <Text strong>Natural Language Query:</Text>
                <QueryText>{dataset.nl_query}</QueryText>
              </>
            )}

            {tableData.columns.length > 0 && (
              <>
                <Text strong>Sample Data:</Text>
                <Table
                  columns={tableData.columns}
                  dataSource={tableData.dataSource}
                  pagination={{ pageSize: 10 }}
                  scroll={{ x: 'max-content' }}
                  size="small"
                  style={{ marginTop: 8 }}
                />
              </>
            )}
          </>
        );
      })()}
    </StyledModal>
  );
}

DatasetModal.propTypes = {
  resultKey: PropTypes.string,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default DatasetModal;
